import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { roleLabel } from "../utils/auth";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCAL_API_URL;

function formatDateTime(dateString) {
  if (!dateString) {
    return "Aldrig";
  }

  return new Intl.DateTimeFormat("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(dateString));
}

function EmptyState({ text }) {
  return (
    <div className="theme-empty-state rounded-[22px] px-4 py-5 text-slate-500">
      {text}
    </div>
  );
}

function RankingList({ title, subtitle, items, renderMeta }) {
  return (
    <section className="theme-card">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">{subtitle}</p>
      <h2 className="mt-2 text-lg font-bold text-slate-800">{title}</h2>

      <div className="mt-4 space-y-3">
        {items.length === 0 && <EmptyState text="Ingen data ännu för vald period." />}
        {items.map((item, index) => (
          <div
            key={`${title}-${item.sessionId || item.clipPath || item.username || index}`}
            className="flex items-start justify-between gap-4 rounded-[22px] border border-sky-200/80 bg-gradient-to-r from-white via-sky-50 to-cyan-50 px-4 py-4 shadow-sm"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sm font-semibold text-sky-700">
                  {index + 1}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-base font-semibold text-slate-800">
                    {item.title || item.displayName || "Okänd användare"}
                  </p>
                  {renderMeta && (
                    <p className="mt-1 break-words text-sm text-slate-500">
                      {renderMeta(item)}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {typeof item.count === "number" && (
              <span className="rounded-full border border-sky-200 bg-white px-3 py-1 text-sm font-semibold text-sky-700">
                {item.count}
              </span>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [analytics, setAnalytics] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    axios.get(`${API_URL}/analytics/overview`, {
      headers: {
        "Content-Type": "application/json",
        token: localStorage.getItem("token")
      },
      params: {
        days,
        inactiveDays: days
      }
    })
      .then((response) => {
        setAnalytics(response.data);
        setErrorMessage("");
      })
      .catch((error) => {
        setErrorMessage(error.response?.data?.message || "Kunde inte hämta analysdata");
      });
  }, [days]);

  const totals = analytics?.totals || {
    sessionRuns: 0,
    sessionViews: 0,
    videoViews: 0,
    inactiveUsers: 0,
    missingMetadata: 0
  };

  return (
    <div className="flex flex-col items-center px-3">
      <div className="w-full max-w-6xl py-4">
        <div className="theme-card">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Administration</p>
              <h1 className="mt-2 text-lg font-bold text-slate-800">Analysöversikt</h1>
              <p className="mt-2 text-sm text-slate-600">
                Följ användning, tittarbeteende, inloggningar och innehåll som saknar metadata.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/admin-dashboard">
                <button className="rounded-[20px] border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-100">
                  Till administration
                </button>
              </Link>
              <select
                value={days}
                onChange={(event) => setDays(parseInt(event.target.value, 10))}
                className="rounded-[20px] border border-sky-200 bg-white px-5 py-3 text-sm text-slate-800 shadow-sm outline-none transition-colors focus:border-sky-400"
              >
                <option value={7}>Senaste 7 dagarna</option>
                <option value={30}>Senaste 30 dagarna</option>
                <option value={90}>Senaste 90 dagarna</option>
              </select>
            </div>
          </div>

          {errorMessage && (
            <div className="theme-alert-error mt-4 py-3">
              {errorMessage}
            </div>
          )}

          <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <div className="theme-panel">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Pass körda</p>
              <p className="mt-3 text-3xl font-bold text-slate-800">{totals.sessionRuns}</p>
            </div>
            <div className="theme-panel">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Pass visade</p>
              <p className="mt-3 text-3xl font-bold text-slate-800">{totals.sessionViews}</p>
            </div>
            <div className="theme-panel">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Videovisningar</p>
              <p className="mt-3 text-3xl font-bold text-slate-800">{totals.videoViews}</p>
            </div>
            <div className="theme-panel">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Inaktiva användare</p>
              <p className="mt-3 text-3xl font-bold text-slate-800">{totals.inactiveUsers}</p>
            </div>
            <div className="theme-panel">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Saknad metadata</p>
              <p className="mt-3 text-3xl font-bold text-slate-800">{totals.missingMetadata}</p>
            </div>
          </div>
        </div>

        {!errorMessage && (
          <div className="mt-5 grid gap-5 xl:grid-cols-2">
            <RankingList
              title="Mest använda pass"
              subtitle="Sessioner"
              items={analytics?.mostUsedSessions || []}
              renderMeta={(item) => `${item.count} körningar under vald period`}
            />
            <RankingList
              title="Mest visade pass"
              subtitle="Sessioner"
              items={analytics?.mostViewedSessions || []}
              renderMeta={(item) => `${item.count} öppningar under vald period`}
            />
            <RankingList
              title="Mest visade videos"
              subtitle="Videor"
              items={analytics?.mostViewedVideos || []}
              renderMeta={(item) => item.clipPath}
            />

            <section className="theme-card">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Inloggningar</p>
              <h2 className="mt-2 text-lg font-bold text-slate-800">Senaste inloggningar</h2>
              <div className="mt-4 space-y-3">
                {(analytics?.recentLogins || []).length === 0 && (
                  <EmptyState text="Inga inloggningar hittades för vald period." />
                )}
                {(analytics?.recentLogins || []).map((login) => (
                  <div key={login._id} className="rounded-[22px] border border-slate-200 bg-white/80 px-4 py-4">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-base font-semibold text-slate-800">{login.displayName || login.username}</p>
                        <p className="mt-1 text-sm text-slate-500">{roleLabel(login.role)}</p>
                      </div>
                      <p className="text-sm font-medium text-sky-700">{formatDateTime(login.loginAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="theme-card">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Användare</p>
              <h2 className="mt-2 text-lg font-bold text-slate-800">Inaktiva användare</h2>
              <p className="mt-2 text-sm text-slate-600">
                Visar användare som inte loggat in de senaste {analytics?.inactiveDays || days} dagarna.
              </p>
              <div className="mt-4 space-y-3">
                {(analytics?.inactiveUsers || []).length === 0 && (
                  <EmptyState text="Inga inaktiva användare i vald period." />
                )}
                {(analytics?.inactiveUsers || []).map((user) => (
                  <div key={user.username} className="rounded-[22px] border border-slate-200 bg-white/80 px-4 py-4">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-base font-semibold text-slate-800">{user.displayName || user.username}</p>
                        <p className="mt-1 text-sm text-slate-500">{roleLabel(user.role)}</p>
                      </div>
                      <p className="text-sm font-medium text-slate-600">
                        Senast aktiv: {formatDateTime(user.lastLoginAt)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="theme-card xl:col-span-2">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Metadata</p>
              <h2 className="mt-2 text-lg font-bold text-slate-800">Saknad metadata</h2>
              <div className="mt-4 grid gap-4 md:grid-cols-3">
                <div className="theme-panel">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Totalt</p>
                  <p className="mt-3 text-2xl font-bold text-slate-800">{analytics?.missingMetadata?.total || 0}</p>
                </div>
                <div className="theme-panel">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Saknar beskrivning</p>
                  <p className="mt-3 text-2xl font-bold text-slate-800">{analytics?.missingMetadata?.missingDescriptions || 0}</p>
                </div>
                <div className="theme-panel">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Används i pass</p>
                  <p className="mt-3 text-2xl font-bold text-slate-800">{analytics?.missingMetadata?.referencedInSessions || 0}</p>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                {(analytics?.missingMetadata?.items || []).length === 0 && (
                  <EmptyState text="Ingen metadata saknas just nu." />
                )}
                {(analytics?.missingMetadata?.items || []).map((item) => (
                  <div key={item.clipPath} className="rounded-[22px] border border-slate-200 bg-white/80 px-4 py-4">
                    <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                      <div className="min-w-0">
                        <p className="truncate text-base font-semibold text-slate-800">{item.title}</p>
                        <p className="mt-1 break-words text-sm text-slate-500">{item.clipPath}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {item.missingTitle && (
                          <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-amber-700">
                            Saknar titel
                          </span>
                        )}
                        {item.missingDescription && (
                          <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-amber-700">
                            Saknar beskrivning
                          </span>
                        )}
                        {item.referencedInSession && (
                          <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-sky-700">
                            Används i pass
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminAnalytics;
